import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { useAuth, canManageOps } from "@/context/AuthContext";
import { RAPPORTINO } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, X, Trash2, Camera, Paperclip, ImageIcon, Sparkles, Loader2, Pencil, Calculator, Unlink } from "lucide-react";

// Tipo Cliente → Aliquota IVA associata (deve rimanere allineato al backend)
const TIPI_CLIENTE = [
  { value: "privato",    label: "Privato",    aliquota: 22 },
  { value: "condominio", label: "Condominio", aliquota: 10 },
  { value: "azienda",    label: "Azienda",    aliquota: 22 },
];
const ALIQUOTA_BY_TIPO = Object.fromEntries(TIPI_CLIENTE.map((t) => [t.value, t.aliquota]));

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const emptyForm = (userName) => ({
  cantiere_id: "",
  data: new Date().toISOString().slice(0, 10),
  responsabile: userName || "",
  lavoratori: [],
  lavorazioni: "",
  tipo_cliente: "privato", // obbligatorio
  imponibile: 0,           // base per IVA previsionale
  problemi: "",
  note: "",
  materiali_utilizzati: "",
  mezzi_utilizzati: "",
  foto_base64: [],
});

export default function Rapportini() {
  const { user } = useAuth();
  const canCreate = !!user;
  const isOps = canManageOps(user);
  // Solo admin/impiegato_amm vede costi, imponibile, tipo cliente e IVA
  const canSeeEcon = user?.role === "amministratore" || user?.role === "impiegato_amministrativo";
  const qc = useQueryClient();

  const { data: rapportini = [] } = useQuery({
    queryKey: ["rapportini-all"],
    queryFn: () => api.get("/rapportini").then((r) => r.data),
  });
  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri-all-for-rapportini"],
    queryFn: () => api.get("/cantieri", { params: { tipo: "all" } }).then((r) => r.data),
  });
  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // rapportino_id in edit
  const [aiExtracting, setAiExtracting] = useState(false);
  const aiExtractedRef = useRef(new Set());
  const [form, setForm] = useState(emptyForm(user?.name));

  // Live calc IVA previsionale
  const aliquotaCorrente = ALIQUOTA_BY_TIPO[form.tipo_cliente] ?? 22;
  const ivaPrevisionale = useMemo(
    () => +(((+form.imponibile || 0) * aliquotaCorrente) / 100).toFixed(2),
    [form.imponibile, aliquotaCorrente],
  );

  const estraiDatiAi = async (dataUrl) => {
    if (!dataUrl) return;
    setAiExtracting(true);
    const t = toast.loading("AI: leggo l'allegato…");
    try {
      const { data } = await api.post("/ai/estrai?tipo=rapportino", { allegato: dataUrl });
      const d = data?.dati || {};
      const filled = Object.values(d).filter((v) => v && String(v).trim().length > 0).length;
      setForm((f) => ({
        ...f,
        data: /^\d{4}-\d{2}-\d{2}$/.test(d.data) ? d.data : f.data,
        lavorazioni: d.lavorazioni ? (f.lavorazioni ? `${f.lavorazioni}\n${d.lavorazioni}` : d.lavorazioni) : f.lavorazioni,
        materiali_utilizzati: d.materiali_utilizzati ? (f.materiali_utilizzati ? `${f.materiali_utilizzati}\n${d.materiali_utilizzati}` : d.materiali_utilizzati) : f.materiali_utilizzati,
        mezzi_utilizzati: d.mezzi_utilizzati ? (f.mezzi_utilizzati ? `${f.mezzi_utilizzati}\n${d.mezzi_utilizzati}` : d.mezzi_utilizzati) : f.mezzi_utilizzati,
        problemi: d.problemi ? (f.problemi ? `${f.problemi}\n${d.problemi}` : d.problemi) : f.problemi,
        note: d.note ? (f.note ? `${f.note}\n${d.note}` : d.note) : f.note,
      }));
      toast.dismiss(t);
      if (filled === 0) {
        toast.warning("L'AI non è riuscita a leggere l'allegato. Riprova con una foto più nitida o compila manualmente.");
      } else {
        toast.success(`✓ ${filled} campi compilati dall'AI — verifica e completa`);
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(e.response?.data?.detail || "Estrazione AI non riuscita");
    } finally {
      setAiExtracting(false);
    }
  };

  // Auto-fill tipo_cliente dal cantiere selezionato (feature "Cliente Anagrafica")
  // Solo alla creazione: se l'utente modifica manualmente il tipo cliente
  // dopo aver scelto il cantiere, il suo valore prevale.
  const autoFilledRef = useRef(null);
  useEffect(() => {
    if (editing) return;
    if (!form.cantiere_id) return;
    if (autoFilledRef.current === form.cantiere_id) return;
    const cant = cantieri.find((c) => c.cantiere_id === form.cantiere_id);
    if (cant?.tipo_cliente) {
      setForm((f) => ({ ...f, tipo_cliente: cant.tipo_cliente }));
      autoFilledRef.current = form.cantiere_id;
      const label = TIPI_CLIENTE.find((t) => t.value === cant.tipo_cliente)?.label;
      toast.success(`Tipo cliente auto-compilato dal cantiere: ${label}`, { duration: 2500 });
    } else {
      autoFilledRef.current = form.cantiere_id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cantiere_id, cantieri, editing]);

  useEffect(() => {
    if (aiExtracting) return;
    if (!form.foto_base64 || form.foto_base64.length === 0) return;
    const last = form.foto_base64[form.foto_base64.length - 1];
    if (!last || aiExtractedRef.current.has(last)) return;
    if (form.lavorazioni && form.lavorazioni.trim().length > 10) {
      aiExtractedRef.current.add(last);
      return;
    }
    aiExtractedRef.current.add(last);
    estraiDatiAi(last);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.foto_base64]);

  const addFiles = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    for (const file of arr) {
      if (file.size > 6 * 1024 * 1024) {
        toast.error(`"${file.name}" troppo grande (max 6 MB)`);
        continue;
      }
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      }).catch(() => null);
      if (b64) setForm((f) => ({ ...f, foto_base64: [...f.foto_base64, b64] }));
    }
  };

  const removeFile = (idx) =>
    setForm((f) => ({ ...f, foto_base64: f.foto_base64.filter((_, i) => i !== idx) }));

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm(user?.name));
  };

  const create = useMutation({
    mutationFn: (payload) => api.post("/rapportini", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success(`Rapportino creato — IVA previsionale ${eur(ivaPrevisionale)} (${aliquotaCorrente}%)`);
      qc.invalidateQueries({ queryKey: ["rapportini-all"] });
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
      closeModal();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/rapportini/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      toast.success("Rapportino aggiornato — IVA previsionale ricalcolata");
      qc.invalidateQueries({ queryKey: ["rapportini-all"] });
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
      closeModal();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore aggiornamento"),
  });

  const sblocca = useMutation({
    mutationFn: (id) => api.post(`/rapportini/${id}/sblocca-fattura`).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`Rapportino ${d.rapportino_id} sbloccato dalla fattura — IVA torna nel Previsionale`);
      qc.invalidateQueries({ queryKey: ["rapportini-all"] });
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
      qc.invalidateQueries({ queryKey: ["fatture"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Sblocco fallito"),
  });

  const removeRap = useMutation({
    mutationFn: (id) => api.delete(`/rapportini/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Rapportino eliminato — spostato nel cestino, IVA Previsionale aggiornata");
      qc.invalidateQueries({ queryKey: ["rapportini-all"] });
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Eliminazione fallita"),
  });

  const submit = () => {
    if (!form.tipo_cliente) {
      toast.error("Seleziona il Tipo Cliente (Privato / Condominio / Azienda)");
      return;
    }
    if (editing) {
      update.mutate({
        id: editing,
        patch: {
          tipo_cliente: form.tipo_cliente,
          imponibile: parseFloat(form.imponibile || 0),
          lavorazioni: form.lavorazioni,
          materiali_utilizzati: form.materiali_utilizzati,
          mezzi_utilizzati: form.mezzi_utilizzati,
          problemi: form.problemi,
          note: form.note,
          responsabile: form.responsabile,
        },
      });
    } else {
      create.mutate(form);
    }
  };

  const openEdit = (r) => {
    setEditing(r.rapportino_id);
    setForm({
      cantiere_id: r.cantiere_id,
      data: r.data,
      responsabile: r.responsabile || "",
      lavoratori: r.lavoratori || [],
      lavorazioni: r.lavorazioni || "",
      tipo_cliente: r.tipo_cliente || "privato",
      imponibile: r.imponibile || 0,
      problemi: r.problemi || "",
      note: r.note || "",
      materiali_utilizzati: r.materiali_utilizzati || "",
      mezzi_utilizzati: r.mezzi_utilizzati || "",
      foto_base64: r.foto_base64 || [],
    });
    setOpen(true);
  };

  const addWorker = () => {
    if (!lavoratori[0]) return;
    setForm((f) => ({
      ...f,
      lavoratori: [
        ...f.lavoratori,
        {
          lavoratore_id: lavoratori[0].lavoratore_id,
          lavoratore_nome: `${lavoratori[0].nome} ${lavoratori[0].cognome}`,
          ore_ordinarie: 8,
          ore_straordinarie: 0,
        },
      ],
    }));
  };

  const updateWorker = (i, key, val) => {
    setForm((f) => {
      const arr = [...f.lavoratori];
      const item = { ...arr[i], [key]: val };
      if (key === "lavoratore_id") {
        const w = lavoratori.find((l) => l.lavoratore_id === val);
        if (w) item.lavoratore_nome = `${w.nome} ${w.cognome}`;
      }
      arr[i] = item;
      return { ...f, lavoratori: arr };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Rapportini</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Rapportini giornalieri</h1>
          <p className="text-xs text-slate-500 mt-1">
            {canSeeEcon ? (
              <>Il <b>Tipo Cliente</b> definisce l'aliquota IVA che verrà inserita nell'IVA Previsionale
              (Privato/Azienda 22% · Condominio 10%). Nel Previsionale confluisce <b>solo</b> l'importo IVA
              calcolato, mai l'intero imponibile del rapportino.</>
            ) : (
              <>Registra le lavorazioni giornaliere, ore e materiali del cantiere.</>
            )}
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => { setEditing(null); setForm(emptyForm(user?.name)); setOpen(true); }}
            data-testid={RAPPORTINO.newButton}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm shadow-brutal"
          >
            <Plus className="w-4 h-4" /> Nuovo rapportino
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Data</th>
              <th className="text-left p-3">Cantiere</th>
              <th className="text-left p-3">Responsabile</th>
              <th className="text-left p-3">Lavoratori</th>
              <th className="text-right p-3">Ore ord.</th>
              <th className="text-right p-3">Ore str.</th>
              {canSeeEcon && <th className="text-left p-3">Tipo cliente</th>}
              {canSeeEcon && <th className="text-right p-3">Imponibile</th>}
              {canSeeEcon && <th className="text-right p-3">IVA prev.</th>}
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rapportini.map((r) => {
              const ord = (r.lavoratori || []).reduce((s, l) => s + parseFloat(l.ore_ordinarie || 0), 0);
              const str = (r.lavoratori || []).reduce((s, l) => s + parseFloat(l.ore_straordinarie || 0), 0);
              const tc = r.tipo_cliente || "privato";
              const aliq = ALIQUOTA_BY_TIPO[tc] ?? 22;
              const iva = r.iva_previsionale_calcolata?.importo_iva ?? +(((r.imponibile || 0) * aliq) / 100).toFixed(2);
              const tipoLabel = TIPI_CLIENTE.find((t) => t.value === tc)?.label || tc;
              const tcColor = tc === "condominio" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : tc === "azienda" ? "bg-indigo-100 text-indigo-800 border-indigo-300"
                : "bg-amber-100 text-amber-800 border-amber-300";
              return (
                <tr key={r.rapportino_id} className={`border-t border-slate-100 hover:bg-slate-50 ${r.fatturato ? "bg-emerald-50/40" : ""}`} data-testid={`row-rap-${r.rapportino_id}`}>
                  <td className="p-3 font-mono">
                    {r.data}
                    {r.fatturato && (
                      <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm" title={`Fatturato · ${r.fattura_emessa_numero || r.fattura_emessa_id}`}>
                        ✓ FATTURATO
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-medium">
                    {r.cantiere_nome}
                    {(() => {
                      const c = cantieri.find((x) => x.cantiere_id === r.cantiere_id);
                      if (c?.tipo === "manutenzione") return (
                        <span className="ml-2 text-[9px] font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm border border-amber-200">🔧 manut.</span>
                      );
                      return null;
                    })()}
                  </td>
                  <td className="p-3">{r.responsabile}</td>
                  <td className="p-3 text-xs text-slate-600">
                    {(r.lavoratori || []).map((l) => l.lavoratore_nome).join(", ")}
                  </td>
                  <td className="p-3 text-right font-mono">{ord}</td>
                  <td className="p-3 text-right font-mono">{str}</td>
                  {canSeeEcon && (
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${tcColor}`}>
                        {tipoLabel} · {aliq}%
                      </span>
                    </td>
                  )}
                  {canSeeEcon && <td className="p-3 text-right font-mono">{eur(r.imponibile)}</td>}
                  {canSeeEcon && (
                    <td className="p-3 text-right font-mono text-orange-700 font-semibold" data-testid={`iva-prev-${r.rapportino_id}`}>
                      {eur(iva)}
                    </td>
                  )}
                  <td className="p-3 text-right">
                    {canSeeEcon && r.fatturato && (
                      <button
                        onClick={() => {
                          if (window.confirm(
                            `Sbloccare questo rapportino dalla fattura ${r.fattura_emessa_numero || r.fattura_emessa_id}?\n\n` +
                            `L'IVA (${eur(iva)}) tornerà nell'IVA Previsionale e il rapportino sarà riassegnabile a un'altra fattura.`
                          )) {
                            sblocca.mutate(r.rapportino_id);
                          }
                        }}
                        data-testid={`btn-sblocca-rap-${r.rapportino_id}`}
                        disabled={sblocca.isPending}
                        title={`Sblocca dalla fattura ${r.fattura_emessa_numero || ""}`}
                        className="mr-1 p-1.5 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-sm disabled:opacity-50"
                      >
                        <Unlink className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(r)}
                      data-testid={`btn-edit-rap-${r.rapportino_id}`}
                      className="p-1.5 border border-slate-300 hover:bg-slate-50 rounded-sm"
                      title="Modifica"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {isOps && (
                      <button
                        onClick={() => {
                          if (window.confirm(
                            `Sei sicuro di voler eliminare il rapportino del ${r.data}?\n\n` +
                            `${r.cantiere_nome ? "Cantiere: " + r.cantiere_nome + "\n" : ""}` +
                            `Il rapportino verrà spostato nel cestino e l'IVA Previsionale ricalcolata automaticamente.\n\n` +
                            `${r.fatturato ? "⚠️ Attenzione: questo rapportino è collegato a una fattura emessa. Sganciarlo prima." : ""}`
                          )) {
                            removeRap.mutate(r.rapportino_id);
                          }
                        }}
                        data-testid={`btn-delete-rap-${r.rapportino_id}`}
                        disabled={removeRap.isPending || r.fatturato}
                        title={r.fatturato ? "Sblocca prima dalla fattura" : "Elimina rapportino"}
                        className="ml-1 p-1.5 border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rapportini.length === 0 && (
              <tr><td colSpan={canSeeEcon ? 10 : 7} className="p-8 text-center text-slate-400">Nessun rapportino</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="bg-white w-full max-w-3xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl">
                {editing ? "Modifica rapportino" : "Nuovo rapportino"}
              </h2>
              <button onClick={closeModal}><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Cantiere / Manutenzione</label>
                  <select
                    value={form.cantiere_id}
                    onChange={(e) => setForm({ ...form, cantiere_id: e.target.value })}
                    data-testid="rap-cantiere-select"
                    disabled={!!editing}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white disabled:bg-slate-50"
                  >
                    <option value="">— seleziona —</option>
                    <optgroup label="🏗️ Cantieri">
                      {cantieri.filter((c) => c.tipo !== "manutenzione").map((c) => (
                        <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>
                      ))}
                    </optgroup>
                    <optgroup label="🔧 Manutenzioni">
                      {cantieri.filter((c) => c.tipo === "manutenzione").map((c) => (
                        <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Data</label>
                  <input type="date" value={form.data}
                    onChange={(e) => setForm({ ...form, data: e.target.value })}
                    disabled={!!editing}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50" />
                </div>
                {canSeeEcon && (
                  <div>
                    <label className="text-xs font-semibold text-slate-600 block mb-1">
                      Tipo cliente <span className="text-rose-600">*</span>
                    </label>
                    <select
                      value={form.tipo_cliente}
                      onChange={(e) => setForm({ ...form, tipo_cliente: e.target.value })}
                      data-testid="rap-tipo-cliente"
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                      required
                    >
                      {TIPI_CLIENTE.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label} — IVA {t.aliquota}%
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Blocco Imponibile + IVA calcolata live — visibile solo ai gestori/admin */}
              {canSeeEcon && (
                <div className="bg-orange-50 border border-orange-200 p-3 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-4 h-4 text-orange-700" />
                  <span className="text-xs font-bold uppercase tracking-widest text-orange-800">
                    Imponibile & IVA previsionale
                  </span>
                </div>
                <div className="grid md:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 block mb-1">Imponibile (€)</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={form.imponibile}
                      onChange={(e) => setForm({ ...form, imponibile: parseFloat(e.target.value || 0) })}
                      data-testid="rap-imponibile"
                      placeholder="es. 10.000,00"
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                    />
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">× Aliquota IVA</div>
                    <div className="font-display font-black text-2xl text-orange-700" data-testid="rap-aliquota">
                      {aliquotaCorrente}%
                    </div>
                    <div className="text-[10px] text-slate-500">
                      da tipo cliente: {TIPI_CLIENTE.find((t) => t.value === form.tipo_cliente)?.label}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">= IVA Previsionale</div>
                    <div className="font-display font-black text-2xl text-orange-700" data-testid="rap-iva-prev">
                      {eur(ivaPrevisionale)}
                    </div>
                    <div className="text-[10px] text-slate-500 italic">
                      questo importo confluisce nell'IVA Previsionale
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-orange-800/80 mt-2 leading-snug">
                  Solo l'IVA <b>calcolata</b> ({eur(ivaPrevisionale)}) verrà inserita nell'IVA Previsionale.
                  L'imponibile del rapportino ({eur(form.imponibile)}) rimane come base di calcolo:
                  se lo modifichi qui, la Contabilità IVA si aggiorna automaticamente.
                </p>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-slate-600">Lavoratori presenti</label>
                  <button
                    onClick={addWorker}
                    data-testid={RAPPORTINO.addWorker}
                    className="text-xs px-2 py-1 border border-slate-300 hover:bg-slate-50 rounded-sm"
                  >
                    + Aggiungi lavoratore
                  </button>
                </div>
                <div className="space-y-2">
                  {form.lavoratori.map((l, i) => (
                    <div key={`${l.lavoratore_id}-${i}`} className="grid grid-cols-12 gap-2 items-center">
                      <select
                        value={l.lavoratore_id}
                        onChange={(e) => updateWorker(i, "lavoratore_id", e.target.value)}
                        className="col-span-6 px-2 py-2 border border-slate-300 rounded-sm text-sm"
                      >
                        {lavoratori.map((w) => (
                          <option key={w.lavoratore_id} value={w.lavoratore_id}>{w.nome} {w.cognome}</option>
                        ))}
                      </select>
                      <div className="col-span-2">
                        <input type="number" step="0.5" placeholder="ore ord." value={l.ore_ordinarie}
                          onChange={(e) => updateWorker(i, "ore_ordinarie", parseFloat(e.target.value || 0))}
                          className="w-full px-2 py-2 border border-slate-300 rounded-sm text-sm" />
                      </div>
                      <div className="col-span-2">
                        <input type="number" step="0.5" placeholder="ore str." value={l.ore_straordinarie}
                          onChange={(e) => updateWorker(i, "ore_straordinarie", parseFloat(e.target.value || 0))}
                          className="w-full px-2 py-2 border border-slate-300 rounded-sm text-sm" />
                      </div>
                      <button
                        onClick={() => setForm((f) => ({ ...f, lavoratori: f.lavoratori.filter((_, j) => j !== i) }))}
                        className="col-span-2 text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" /> Rimuovi
                      </button>
                    </div>
                  ))}
                  {form.lavoratori.length === 0 && <div className="text-slate-400 text-sm">Nessun lavoratore aggiunto</div>}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Lavorazioni eseguite</label>
                <textarea rows={3} value={form.lavorazioni} onChange={(e) => setForm({ ...form, lavorazioni: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Materiali utilizzati</label>
                  <textarea rows={2} value={form.materiali_utilizzati} onChange={(e) => setForm({ ...form, materiali_utilizzati: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Mezzi utilizzati</label>
                  <textarea rows={2} value={form.mezzi_utilizzati} onChange={(e) => setForm({ ...form, mezzi_utilizzati: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Problemi riscontrati</label>
                  <textarea rows={2} value={form.problemi} onChange={(e) => setForm({ ...form, problemi: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
                  <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
              </div>

              {!editing && (
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-2">
                    Foto e allegati ({form.foto_base64.length})
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-violet-400 bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white rounded-sm text-sm cursor-pointer font-semibold shadow-brutal">
                      <Sparkles className="w-4 h-4" />
                      <span>Inserimento AI (foto o PDF)</span>
                      <input type="file" accept="image/*,application/pdf" capture="environment"
                        onChange={(e) => addFiles(e.target.files)} className="hidden" data-testid="rapportino-ai-input" />
                    </label>
                    <label className="flex items-center justify-center gap-2 px-3 py-2 border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-sm text-sm cursor-pointer">
                      <Camera className="w-4 h-4" />
                      <span>Scatta foto</span>
                      <input type="file" accept="image/*" capture="environment" multiple
                        onChange={(e) => addFiles(e.target.files)} className="hidden" data-testid="rapportino-camera-input" />
                    </label>
                    <label className="flex items-center justify-center gap-2 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 rounded-sm text-sm cursor-pointer">
                      <Paperclip className="w-4 h-4" />
                      <span>Carica PDF</span>
                      <input type="file" accept="image/*,application/pdf" multiple
                        onChange={(e) => addFiles(e.target.files)} className="hidden" data-testid="rapportino-file-input" />
                    </label>
                  </div>
                  <div className="mt-2 text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded-sm p-2 inline-flex items-center gap-1">
                    {aiExtracting ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> <b>AI in corso…</b> compilo lavorazioni, materiali, mezzi dalla foto.</>
                    ) : (
                      <><Sparkles className="w-3.5 h-3.5" /> Clicca <b className="mx-1">Inserimento AI</b> per compilare automaticamente lavorazioni, materiali, mezzi e problemi dalla foto o dal PDF.</>
                    )}
                  </div>
                  {form.foto_base64.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {form.foto_base64.map((b64, i) => {
                        const isPdf = b64.startsWith("data:application/pdf");
                        const key = `${b64.slice(0, 40)}-${i}`;
                        return (
                          <div key={key} className="relative border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
                            {isPdf ? (
                              <div className="h-24 flex flex-col items-center justify-center text-slate-500 gap-1">
                                <Paperclip className="w-6 h-6" />
                                <span className="text-[10px] font-mono">PDF</span>
                              </div>
                            ) : (
                              <img src={b64} alt="" className="h-24 w-full object-cover" />
                            )}
                            <button type="button" onClick={() => removeFile(i)}
                              data-testid={`rapportino-remove-file-${i}`}
                              className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-white text-rose-600 rounded-sm border border-slate-200"
                              title="Rimuovi">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {form.foto_base64.length === 0 && (
                    <div className="mt-2 text-xs text-slate-400 flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" /> Nessun allegato — puoi scattare foto direttamente dal telefono.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={closeModal} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">Annulla</button>
              <button
                onClick={submit}
                disabled={create.isPending || update.isPending}
                data-testid={RAPPORTINO.saveButton}
                className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm"
              >
                {editing ? "Salva modifiche" : "Salva rapportino"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
